<template>
  <header :class="`header header-${theme}`">
    <div class="header-top container mx-auto">
      <div class="logo" v-if="xApi == 'mosaic'">
        <a class="logo-ascenti" href="https://ascenti.co.uk">
          <img v-if="theme === 'dark'" src="../../assets/images/ascenti-mosaic-logo-white.png" alt="Ascenti Logo">
          <img v-else src="../../assets/images/ascenti-mosaic-logo.png" alt="Ascenti Logo">
        </a>
        <a class="logo-ascenti-mobile" href="https://ascenti.co.uk">
          <img v-if="theme === 'dark'" src="../../assets/images/ascenti-mosaic-logo-white.png" width="113" alt="Ascenti Logo">
          <img v-else src="../../assets/images/ascenti-mosaic-logo.png" alt="Ascenti Logo">
        </a>
      </div>
      <div class="logo" v-else>
        <a class="logo-ascenti" href="https://ascenti.co.uk">
          <img v-if="theme === 'dark'" src="../../assets/images/ascenti-logo-white.png" alt="Ascenti Logo">
          <img v-else src="../../assets/images/logo-ascenti.svg" alt="Ascenti Logo">
        </a>
        <a class="logo-ascenti-mobile" href="https://ascenti.co.uk">
          <img src="../../assets/images/logo-ascenti-mobile.svg" alt="Ascenti Logo">
        </a>
        <a v-if="partnerLogoPath" target="_blank" :href="partnerLogoLink">
          <img class="logo-partner" :src="partnerLogoPath" alt="Partner Logo">
        </a>
      </div>

      <div class="hamburger" :class="{'open': isActive}" @click="isActive = !isActive">
        <div></div>
        <div></div>
        <div></div>
      </div>

      <nav class="navigation" :class="{'open': isActive}">
        <ul>
          <li v-if="showTreatmentLink"><a href="https://www.ascenti.co.uk/treatment" target="_blank">Our treatments & services</a></li>
          <li v-if="showVisitClinic"><router-link to="/">Visit a clinic</router-link></li>
          <li v-if="showVideoAppointment"><router-link to="/video">Video appointment</router-link></li>
          <li v-if="showSymptomChecker"><a href="https://checker.ascenti.co.uk/">Symptom checker</a></li>
        </ul>
      </nav>

      <div class="secondary-navigation" v-if="isBooking">
        <button @click.prevent="sendCancelReservedAppointmentRequest" role="button">Change Appointment</button>
      </div>
    </div>

    <div class="header-bottom" v-if="component !== null">
      <component :is="component.name" v-bind="component.props || {}"/>
    </div>
  </header>
</template>

<script>
import {mapActions, mapState} from 'vuex';

import Logo from './Logo';
import Navigation from './Navigation';
import Tabs from '../Checkout/Tabs';
import AppointmentOverview from '../AppointmentConfirmation/AppointmentOverview';
import AppointmentsHeader from '../Block/AppointmentsHeader';
import AscentiAppointmentsHeader from '../Block/AscentiAppointmentsHeader';

export default {
  name: 'top',

  data() {
    return {
      isActive: false,
      headerComponent: null
    };
  },

  components: {
    Logo,
    Navigation,
    Tabs,
    AppointmentOverview,
    AppointmentsHeader: process.env.VUE_APP_PARTNER_NAME ? AppointmentsHeader : AscentiAppointmentsHeader,
  },

  props: {
    showNav: Boolean
  },

  computed: {
    ...mapState('header', [
      'component',
      'showNavigation',
      'theme',
    ]),

    ...mapState('appointment', [
      'appointmentData',
    ]),

    isBooking() {
      return this.$route.name === 'booking';
    },

    showTreatmentLink() {
      return process.env.VUE_APP_NAV_SHOW_TREATMENTS_LINK === 'true'
    },

    showVisitClinic() {
      return process.env.VUE_APP_NAV_SHOW_VISIT_CLINIC === 'true'
    },

    showVideoAppointment() {
      return process.env.VUE_APP_NAV_SHOW_VIDEO_APPOINTMENT === 'true'
    },

    showSymptomChecker() {
      return process.env.VUE_APP_NAV_SHOW_SYMPTOM_CHECKER === 'true'
    },

    partnerLogoPath() {
      const logoPath = process.env.VUE_APP_PARTNER_LOGO_PATH;
      if (!logoPath) return null;

      // Ensure Webpack processes the path correctly
      return require(`@/assets/images/${logoPath.split('/').pop()}`);
    },

    partnerLogoLink() {
      return process.env.VUE_APP_PARTNER_LOGO_LINK;
    },

    xApi() {
      return process.env.VUE_APP_X_API;
    }
  },

  methods: {
    ...mapActions('appointments', [
      'cancelReservedAppointment',
    ]),

    sendCancelReservedAppointmentRequest() {
      const appointmentId = this.appointmentData.appointment.AppointmentID;
      const callback = () => this.$router.push({name: 'home'});
      this.cancelReservedAppointment({appointmentId, callback});
    },
  },

  watch: {
    $route(to) {
      if (to.name !== 'home') {
        this.isActive = false;
      }
    }
  },

  created() {
    this.headerComponent = process.env.VUE_APP_PARTNER_NAME ? 'AppointmentsHeader' : 'AscentiAppointmentsHeader';
  }
};
</script>

<style lang="scss">
.header-top {
  padding: 20px 20px;

  @media (min-width: 768px) {
    padding: 0;
    display: flex;
  }

  &.container {
    max-width: 1280px;
  }

  .logo {
    display: flex;
    align-items: center;
    column-gap: 24px;
    float: left;

    img {
      max-height: 64px;
      max-width: 164px;
    }

    @media (min-width: 768px) {
      column-gap: 40px;
      img {
        max-height: 104px;
        max-width: 204px;
      }
    }
  }

  .logo-ascenti {
    display: none;

    @media (min-width: 768px) {
      display: block;
      float: left;
    }
  }

  .logo-ascenti-mobile {
    display: block;
    float: left;

    @media (min-width: 768px) {
      display: none;
    }
  }

  .logo-partner {
    max-height: 66px !important;
    padding-top: 4px;
  }

  .hamburger {
    width: 40px;
    height: 30px;
    float: right;
    cursor: pointer;
    position: relative;
    transform: rotate(0deg);
    transition: .5s ease-in-out;
    z-index: 110;

    @media (min-width: 768px) {
      display: none;
    }

    div {
      display: block;
      position: absolute;
      opacity: 1;
      left: 0;
      transform: rotate(0deg);
      transition: .25s ease-in-out;
      background: #FC6B40;
      height: 6px;
      width: 100%;
      border-radius: 6px;

      &:nth-child(1) {
        top: 0px;
        transform-origin: left center;
      }

      &:nth-child(2) {
        top: 12px;
        transform-origin: left center;
      }

      &:nth-child(3) {
        top: 24px;
        transform-origin: left center;
      }
    }

    &.open div {
      background: #fff;
    }

    &.open div:nth-child(1) {
      transform: rotate(45deg);
      top: -2px;
      left: 6px;
    }

    &.open div:nth-child(2) {
      width: 0%;
      opacity: 0;
    }

    &.open div:nth-child(3) {
      transform: rotate(-45deg);
      top: 26px;
      left: 5px;
    }
  }

  .navigation {
    display: none;
    position: absolute;
    width: 100vw;
    height: 100vh;
    background: linear-gradient(to right, #ffccf6 0%, #ff7ea2 50%, #ff8b02 100%);
    z-index: 99;
    margin-top: -20px;
    margin-left: -20px;

    &.open {
      display: block;
    }

    @media (min-width: 768px) {
      display: block;
      float: left;
      display: flex;
      align-items: center;
      position: static;
      width: auto;
      height: auto;
      background: transparent;
      z-index: 0;
      margin-top: 0;
      margin-left: 0;
    }

    ul {
      margin: 70px auto;
      max-width: 320px;
      text-align: center;

      @media (min-width: 768px) {
        margin: 0;
        max-width: none;
        text-align: left;
      }
    }

    li {
      font-size: 20px;
      display: block;
      font-weight: bold;
      font-size: 32px;
      margin-top: 12px;
      margin-bottom: 12px;

      @media (min-width: 768px) {
        font-weight: normal;
        display: inline;
        font-size: 18px;
        margin-left: 36px;
      }

      @media (min-width: 1024px) {
        margin-left: 68px;
        font-size: 20px;
      }

      a {
        text-decoration: none;
        color: #fff;

        @media (min-width: 768px) {
          color: #145E7F;
        }

        &.router-link-exact-active, &:hover {
          text-decoration: underline;
        }
      }
    }
  }

  .secondary-navigation {
    display: none;

    @media (min-width: 768px) {
      color: #656565;
      font-size: 13px;
      text-decoration: underline;
      display: flex;
      align-items: center;
      margin-left: auto;
      margin-right: 20px;
    }

    @media (min-width: 1024px) {
      font-size: 16px;
    }
  }
}
</style>